import { clsx } from "clsx";
import { Button } from "components/ui/Button";
import { LightAction } from "components/ui/LightAction";
import { createIcon } from "components/icons/IconFactory";
import { AddIcon, TrashIcon } from "components/icons/Icons";
import { FieldArray, FieldArrayRenderProps } from "formik";
import { CSSProperties, useMemo } from "react";
import { DynamicArrayField } from "types/form/componentConfig";
import { FormField } from "components/forms/FormField";
import { useInputRegistry, warnMissingRenderer } from "factories/input-factory/registry";
import { RowInputRenderer } from "factories/input-factory/types";

export interface DynamicArrayProps {
  name: string;
  label?: string;
  fields: DynamicArrayField[];
  gridCols?: number
  minItems?: number;
  maxItems?: number;
  addButtonText?: string;
  removeButtonText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  initialItem?: Record<string, any>;
  className?: string;
  /** Compact table rendering (column header + badge/inputs/delete rows). */
  compact?: boolean;
  emptyConfig?: {
    title: string
    description?: string
    iconKey?: string
  }
}

interface DynamicArrayItemProps {
  fields: DynamicArrayField[];
  index: number;
  values: any[];
  arrayHelpers: FieldArrayRenderProps;
  onRemove: () => void;
  canRemove: boolean;
  gridCols: number
  itemClassName?: string;
  compact?: boolean;
  gridStyle?: CSSProperties;
}

const isNumberField = (field: DynamicArrayField) => 'type' in field && field.type === 'number';

const DynamicArrayItem: React.FC<DynamicArrayItemProps> = ({
  fields,
  index,
  values,
  arrayHelpers,
  onRemove,
  canRemove,
  gridCols,
  itemClassName,
  compact,
  gridStyle,
}) => {
  const { rowRenderers } = useInputRegistry();

  /* Every sub-field goes through the row registry (core text inputs,
     feature dropdowns…): the control is delegated, the row mechanics
     (wrapper, colSpan, compact resolution) stay here. */
  const renderField = (field: DynamicArrayField) => {
    const render = rowRenderers[field.componentKey] as RowInputRenderer<typeof field.componentKey> | undefined;
    if (!render) {
      warnMissingRenderer(field.componentKey, field.name);
      return null;
    }
    const control = render(field, {
      value: values[index]?.[field.name] ?? '',
      onChange: (newValue) => {
        arrayHelpers.replace(index, {
          ...values[index],
          [field.name]: newValue
        });
      },
      label: compact ? undefined : field.label,
      required: compact ? false : field.required,
    });
    if (!control) return null;
    return (
      <div key={field.name} className={compact ? undefined : `col-span-${field.colSpan}`}>
        {control}
      </div>
    );
  };

  if (compact) {
    return (
      <div
        className="grid items-center gap-3 px-4 py-2"
        style={gridStyle}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-sm font-medium text-primary">
          {index + 1}
        </span>
        {fields.map(renderField)}
        <div className="flex justify-center">
          {canRemove && (
            <button
              type="button"
              className="p-1 text-gray-400 transition-colors hover:text-red-500"
              onClick={onRemove}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("flex items-center gap-4", itemClassName)}>
      <div className={`flex-1 grid grid-cols-${gridCols} gap-4`}>
        {fields.map(renderField)}
      </div>
      {canRemove && (
        <Button
          type="button"
          color="secondary"
          onClick={onRemove}
        >
          <TrashIcon />
        </Button>
      )}
    </div>
  );
};

export const DynamicArray: React.FC<DynamicArrayProps> = ({
  name,
  label,
  fields,
  gridCols,
  minItems = 0,
  maxItems = 10,
  addButtonText = "Ajouter un élément",
  removeButtonText = "Supprimer",
  error,
  required,
  disabled,
  initialItem = {},
  className,
  compact,
  emptyConfig
}) => {
  const getInitialItem = () => {
    const item = { ...initialItem };
    fields.forEach(field => {
      if (!(field.name in item)) {
        item[field.name] = isNumberField(field) ? 0 : '';
      }
    });
    return item;
  };

  const emptyIcon = useMemo(() => {
    if (emptyConfig?.iconKey) {
      const Icon = createIcon(emptyConfig.iconKey)
      return <Icon className="h-16 w-16 text-gray-300 mb-4" />
    }
    return <></>
  }, [emptyConfig])

  if (compact) {
    const gridStyle: CSSProperties = {
      gridTemplateColumns: `2.5rem repeat(${fields.length}, minmax(0, 1fr)) 2.5rem`
    };
    return (
      <FormField label={label} error={error} required={required} name={name}>
        <FieldArray name={name}>
          {(arrayHelpers) => {
            const values = arrayHelpers.form.values[name] || [];
            return (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                {/* Column header */}
                <div
                  className="grid items-center gap-3 border-b border-gray-200 px-4 py-2.5"
                  style={gridStyle}
                >
                  <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                    #
                  </span>
                  {fields.map((field) => (
                    <span
                      key={field.name}
                      className="text-xs font-medium uppercase tracking-wide text-gray-500"
                    >
                      {field.label}
                    </span>
                  ))}
                  <span />
                </div>

                {/* Rows */}
                {values.map((item: any, index: number) => (
                  <DynamicArrayItem
                    key={index}
                    fields={fields}
                    index={index}
                    values={values}
                    arrayHelpers={arrayHelpers}
                    gridCols={fields.length}
                    onRemove={() => arrayHelpers.remove(index)}
                    canRemove={!disabled && values.length > minItems}
                    compact
                    gridStyle={gridStyle}
                  />
                ))}

                {/* Add */}
                {!disabled && values.length < maxItems && (
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 border-t border-gray-200 py-3 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
                    onClick={(e) => {
                      e.preventDefault();
                      arrayHelpers.push(getInitialItem());
                    }}
                  >
                    <AddIcon className="h-4 w-4" />
                    {addButtonText}
                  </button>
                )}
              </div>
            );
          }}
        </FieldArray>
      </FormField>
    );
  }

  return (
    <FormField
      label={label}
      error={error}
      required={required}
      name={name}
    >
      <div className={clsx("space-y-4", className)}>
        <FieldArray name={name}>
          {(arrayHelpers) => {
            const values = arrayHelpers.form.values[name] || [];
            const currentGridCols = gridCols || fields.length
            return (
              <div className="space-y-4">
                {values.length > 0 && (
                  <div className="space-y-3">
                    {values.map((item: any, index: number) => (
                      <DynamicArrayItem
                        key={index}
                        fields={fields}
                        index={index}
                        values={values}
                        arrayHelpers={arrayHelpers}
                        gridCols={currentGridCols}
                        onRemove={() => arrayHelpers.remove(index)}
                        canRemove={!disabled && values.length > minItems}
                      />
                    ))}
                  </div>
                )}
                {values.length === 0 && minItems === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-center rounded-xl border bg-white">
                    {emptyIcon}
                    {
                      emptyConfig?.title &&
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {emptyConfig.title}
                      </h3>
                    }
                    {
                      emptyConfig?.description &&
                      <p className="text-gray-500 mb-6 max-w-md">
                        {emptyConfig.description}
                      </p>
                    }
                    <LightAction
                      icon={AddIcon}
                      label={addButtonText}
                      onClick={() => arrayHelpers.push(getInitialItem())}
                    />
                  </div>
                )}
                {!disabled && values.length > 0 && values.length < maxItems && (
                  <button
                    className="w-full flex justify-center bg-white hover:bg-primary/5 text-primary gap-4 border-2 border-primary/20 hover:border-primary/50 rounded-md border-dashed p-2"
                    onClick={(e) => {
                      e.preventDefault()
                      arrayHelpers.push(getInitialItem())
                    }}
                  >
                    <LightAction
                      icon={AddIcon}
                      label={addButtonText}
                    />
                  </button>
                )}
              </div>
            );
          }}
        </FieldArray>
      </div>
    </FormField>
  );
};
